import { Menu, X } from "lucide-react"
import { AnimatePresence, motion } from "framer-motion"
import { Link, useNavigate } from "react-router-dom"
import { useLanguage } from "../context/LanguageContext"
import { useState } from "react"
import { fadeDown, staggerContainer } from "../lib/motion"

const KSA_SITE_URL = "https://ksa.nowpay.com/"

const Navbar = ({isHomePage = false}) => {
    const { translation: t } = useLanguage()
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const navigate = useNavigate()
    return (
        <div className="relative">
            <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-10 bg-white sm:hidden" />
            {/* Main Navigation */}
            <motion.nav
                className={`relative z-10 p-5 md:p-7 lg:p-9 xl:p-10 -mb-0.5 xl:mb-0 bg-secondary rounded-[1.25rem] lg:rounded-[1.875rem]`}
                variants={fadeDown}
                initial="hidden"
                animate="show"
            >
                <motion.div
                    className={`flex ${isHomePage ? 'items-start' : 'items-center'} justify-between`}
                    variants={staggerContainer(0.1, 0.05)}
                    initial="hidden"
                    animate="show"
                >
                    {/* Logo */}
                    <motion.div className="flex items-center" variants={fadeDown}>
                        <Link to="/" aria-label="NowPay home">
                            <img src="/logos/logo.svg" alt="NowPay" className="h-8 md:h-[3.25rem] w-auto hidden lg:block" />
                            <img src="/logos/res-logo.svg" alt="NowPay" className="h-8 md:h-12 w-auto block lg:hidden" />
                        </Link>
                    </motion.div>

                    {/* Desktop Menu */}
                    <motion.div className="flex items-center gap-4 xl:gap-8" variants={staggerContainer(0.08, 0.04)}>
                        <motion.div className="hidden md:flex items-center gap-4 xl:gap-8 md:text-lg xl:text-xl text-primary font-semibold" variants={staggerContainer(0.06, 0.03)}>
                            <motion.div variants={fadeDown}>
                                <Link to="/" className="hover:text-hoverColor transition-colors link-underline">
                                    {t.home}
                                </Link>
                            </motion.div>
                            <motion.div variants={fadeDown}>
                                <Link to="/about-us" className="hover:text-hoverColor transition-colors link-underline">
                                    {t.aboutUs}
                                </Link>
                            </motion.div>
                            <motion.div variants={fadeDown}>
                                <Link to="/faq" className="hover:text-hoverColor transition-colors link-underline">
                                    {t.faq}
                                </Link>
                            </motion.div>
                            <motion.div variants={fadeDown}>
                                <Link to="/in-the-news" className="hover:text-hoverColor transition-colors link-underline">
                                    {t.inTheNews}
                                </Link>
                            </motion.div>
                            <motion.div variants={fadeDown}>
                                <Link to="https://careers.nowpay.cash/jobs/Careers" className="hover:text-hoverColor transition-colors link-underline" target="_blank" rel="noopener noreferrer">
                                    {t.careers}
                                </Link>
                            </motion.div>
                        </motion.div>

                        <motion.button
                            className="flex items-center justify-center bg-primary text-white text-xs sm:text-sm md:text-base xl:text-xl px-6 py-2.5 xl:py-3.5 rounded-full font-semibold hover:bg-hoverColor transition-colors hover-glow"
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.98 }}
                            variants={fadeDown}
                            onClick={() => navigate("/download")}
                        >
                            {t.downloadApp}
                        </motion.button>
                    </motion.div>

                    {/* Mobile Menu Button */}
                    <motion.button
                        className="md:hidden text-primary hover-scale"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        whileTap={{ scale: 0.95 }}
                        variants={fadeDown}
                    >
                        {mobileMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-7" />}
                    </motion.button>
                </motion.div>

                {/* Mobile Menu */}
                <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        className="absolute inset-x-0 top-0 z-[60] h-[90vh] bg-primary text-white rounded-[1.25rem] mb-10 md:hidden pb-4 border-t border-gray-200 pt-4"
                        initial={{ opacity: 0, y: 12, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.98 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                        <div className="flex justify-between items-center px-4">
                            <Link to="/" aria-label="NowPay home" onClick={() => setMobileMenuOpen(false)}>
                                <img
                                    src="/logos/mobile-logo.svg"
                                    alt="NowPay"
                                    className="h-8 md:h-12 w-auto"
                                />
                            </Link>
                            <button className="flex items-center justify-center text-primary bg-white px-6 py-2.5 rounded-full font-semibold w-fit text-xs sm:text-sm md:text-base hover-glow" onClick={() => navigate("/download")}>
                                {t.downloadApp}
                            </button>
                            <button className="md:hidden text-white hover-scale" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                                {mobileMenuOpen && <X className="w-8 h-8" />}
                            </button>
                        </div>
                        <div className="h-full flex flex-col justify-center items-center gap-11 text-lg font-semibold">
                            <Link to="/" className="link-underline" onClick={() => setMobileMenuOpen(false)}>
                                {t.home}
                            </Link>
                            <Link to="/about-us" className="hover:text-hoverColor transition-colors link-underline" onClick={() => setMobileMenuOpen(false)}>
                                {t.aboutUs}
                            </Link>
                            <Link to="/faq" className="hover:text-hoverColor transition-colors link-underline" onClick={() => setMobileMenuOpen(false)}>
                                {t.faq}
                            </Link>
                            <Link to="/in-the-news" className="hover:text-hoverColor transition-colors link-underline" onClick={() => setMobileMenuOpen(false)}>
                                {t.inTheNews}
                            </Link>
                            <Link to="https://careers.nowpay.cash/jobs/Careers" className="hover:text-hoverColor transition-colors link-underline" onClick={() => setMobileMenuOpen(false)} target="_blank" rel="noopener noreferrer">
                                {t.careers}
                            </Link>
                            <Link to={KSA_SITE_URL} className="hover:text-hoverColor transition-colors link-underline" onClick={() => setMobileMenuOpen(false)} target="_blank" rel="noopener noreferrer">
                                {t.visitSaudiSite}
                            </Link>
                        </div>
                    </motion.div>
                )}
                </AnimatePresence>
            </motion.nav>
        </div>
    )
}

export { Navbar }